import { Outlet, NavLink, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";

function NavItem({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-zinc-800 text-zinc-100"
            : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

const PLATFORM_APPS = [
  { label: "Dashboard", href: "https://blazeboard.co/dashboard" },
  { label: "Cloud Admin", href: "https://app.blazeboard.co" },
  { label: "Releases", href: "https://releases.blazeboard.co", active: true },
];

function PlatformBar() {
  return (
    <div className="w-full border-b border-white/[0.06] bg-[#0c0c0c]">
      <div className="mx-auto flex h-8 max-w-7xl items-center justify-between px-4 text-[11px]">
        <div className="flex items-center gap-1">
          {PLATFORM_APPS.map((app) => (
            <a
              key={app.label}
              href={app.href}
              className={`rounded px-2 py-0.5 transition-colors ${
                app.active
                  ? "bg-white/[0.08] text-white font-medium"
                  : "text-white/40 hover:text-white/70 hover:bg-white/[0.04]"
              }`}
            >
              {app.label}
            </a>
          ))}
        </div>
        <div className="text-white/25">blazeboard.co</div>
      </div>
    </div>
  );
}

export function Layout() {
  const { status, email, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-dvh flex flex-col">
      <PlatformBar />
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="flex items-center gap-2 text-lg font-bold tracking-tight"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="w-7 h-7 text-blue-500"
                aria-hidden="true"
              >
                <rect
                  x="2"
                  y="3"
                  width="20"
                  height="14"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M8 21h8M12 17v4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="7"
                  y="7"
                  width="10"
                  height="6"
                  rx="1"
                  fill="currentColor"
                  opacity="0.3"
                />
              </svg>
              <span>Blazeboard</span>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              <NavItem to="/setup">Setup</NavItem>
              <NavItem to="/releases">Releases</NavItem>
              {status === "authenticated" && (
                <NavItem to="/devices">Devices</NavItem>
              )}
            </nav>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {status === "authenticated" && email ? (
              <>
                <span className="text-sm text-zinc-400">{email}</span>
                <button
                  onClick={signOut}
                  className="text-sm text-zinc-400 hover:text-zinc-100 px-3 py-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
                >
                  Sign out
                </button>
              </>
            ) : status === "unauthenticated" ? (
              <a
                href={`https://blazeboard.co/login?redirect=${encodeURIComponent(window.location.href)}`}
                className="text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Sign in
              </a>
            ) : null}
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? (
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-zinc-800 px-4 py-3 space-y-1 animate-fade-in">
            <NavLink
              to="/setup"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-zinc-800 text-zinc-100" : "text-zinc-400"}`
              }
            >
              Setup
            </NavLink>
            <NavLink
              to="/releases"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-zinc-800 text-zinc-100" : "text-zinc-400"}`
              }
            >
              Releases
            </NavLink>
            {status === "authenticated" && (
              <NavLink
                to="/devices"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-zinc-800 text-zinc-100" : "text-zinc-400"}`
                }
              >
                Devices
              </NavLink>
            )}
            <div className="pt-2 border-t border-zinc-800">
              {status === "authenticated" && email ? (
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm text-zinc-400">{email}</span>
                  <button
                    onClick={signOut}
                    className="text-sm text-zinc-400 hover:text-zinc-100"
                  >
                    Sign out
                  </button>
                </div>
              ) : status === "unauthenticated" ? (
                <a
                  href={`https://blazeboard.co/login?redirect=${encodeURIComponent(window.location.href)}`}
                  className="block text-center text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white px-4 py-2.5 rounded-lg"
                >
                  Sign in
                </a>
              ) : null}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-zinc-800 py-8 mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <div>
            <a
              href="https://blazeboard.co"
              className="hover:text-zinc-300 transition-colors"
            >
              Blazeboard
            </a>{" "}
            by{" "}
            <a
              href="https://dennon.dev"
              className="hover:text-zinc-300 transition-colors"
            >
              Dennon Dev
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://blazeboard.co/help"
              className="hover:text-zinc-300 transition-colors"
            >
              Help
            </a>
            <a
              href="https://blazeboard.co/privacy"
              className="hover:text-zinc-300 transition-colors"
            >
              Privacy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
